import React, { useState, useMemo } from 'react';
import { Users, Minus, Plus, Lock, Crown } from 'lucide-react';

export const ServingsAdjuster = ({ 
  originalServings, 
  ingredients, 
  isPremium, 
  onUpgrade 
}) => {
  const [servings, setServings] = useState(originalServings || 4);

  const adjustedIngredients = useMemo(() => {
    if (!ingredients || originalServings === 0) return ingredients;
    
    const ratio = servings / originalServings;
    
    return ingredients.map(ing => ({
      ...ing,
      adjustedAmount: Math.round((ing.amount * ratio) * 100) / 100
    }));
  }, [ingredients, servings, originalServings]);

  const decrease = () => {
    if (servings > 1) setServings(servings - 1);
  };

  const increase = () => {
    if (servings < 50) setServings(servings + 1);
  };

  // If not premium, show locked version with golden styling
  if (!isPremium) {
    return (
      <div className="relative">
        <div className="bg-gradient-to-br from-amber-50 to-yellow-50 dark:from-amber-900/20 dark:to-yellow-900/20 rounded-[32px] p-6 border-2 border-amber-200 dark:border-amber-700/50 shadow-lg shadow-amber-100/50 dark:shadow-amber-900/20">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-to-br from-amber-400 to-yellow-500 rounded-xl flex items-center justify-center text-white shadow-lg shadow-amber-400/30">
                <Crown size={22} />
              </div>
              <div>
                <p className="text-base font-black text-amber-800 dark:text-amber-300">Ajustar Raciones</p>
                <p className="text-xs text-amber-600 dark:text-amber-400 font-bold">Función Premium</p>
              </div>
            </div>
            <div className="flex items-center gap-1 opacity-50">
              <button disabled className="w-10 h-10 rounded-xl bg-amber-200 dark:bg-amber-800/50 text-amber-400 dark:text-amber-600">
                <Minus size={18} className="mx-auto" />
              </button>
              <span className="w-14 text-center text-xl font-black text-amber-400 dark:text-amber-600">{originalServings}</span>
              <button disabled className="w-10 h-10 rounded-xl bg-amber-200 dark:bg-amber-800/50 text-amber-400 dark:text-amber-600">
                <Plus size={18} className="mx-auto" />
              </button>
            </div>
          </div>
          
          <button
            onClick={onUpgrade}
            className="w-full py-4 bg-gradient-to-r from-amber-500 via-yellow-500 to-amber-500 text-white rounded-2xl font-black uppercase tracking-widest text-[11px] flex items-center justify-center gap-2 shadow-xl shadow-amber-500/30 hover:shadow-amber-500/50 hover:scale-[1.02] transition-all border border-amber-400"
          >
            <Crown size={18} /> Desbloquear con Premium
          </button>
          
          <p className="text-center text-[10px] text-amber-600 dark:text-amber-400 mt-3 font-bold">
            Ajusta las cantidades de ingredientes para cualquier número de comensales
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-stone-900 rounded-[32px] p-6 border border-stone-100 dark:border-stone-800 shadow-sm">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center text-primary">
            <Users size={20} />
          </div>
          <div>
            <p className="text-sm font-black text-stone-900 dark:text-white">Ajustar Raciones</p>
            <p className="text-[10px] text-stone-400">
              Original: {originalServings} {originalServings === 1 ? 'persona' : 'personas'}
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-1">
          <button
            data-testid="decrease-servings"
            onClick={decrease}
            disabled={servings <= 1}
            className={`w-10 h-10 rounded-xl transition-all flex items-center justify-center ${
              servings <= 1 
                ? 'bg-stone-100 dark:bg-stone-800 text-stone-300 cursor-not-allowed' 
                : 'bg-stone-100 dark:bg-stone-800 text-stone-600 dark:text-stone-300 hover:bg-primary hover:text-white'
            }`}
          >
            <Minus size={18} />
          </button>
          
          <div className="w-14 text-center">
            <span className="text-2xl font-black text-stone-900 dark:text-white">{servings}</span>
          </div>
          
          <button
            data-testid="increase-servings"
            onClick={increase}
            disabled={servings >= 50}
            className={`w-10 h-10 rounded-xl transition-all flex items-center justify-center ${
              servings >= 50 
                ? 'bg-stone-100 dark:bg-stone-800 text-stone-300 cursor-not-allowed' 
                : 'bg-stone-100 dark:bg-stone-800 text-stone-600 dark:text-stone-300 hover:bg-primary hover:text-white'
            }`}
          >
            <Plus size={18} />
          </button>
        </div>
      </div>

      {servings !== originalServings && (
        <div className="mb-4 px-4 py-2 bg-primary/5 rounded-xl">
          <p className="text-[10px] font-black uppercase tracking-widest text-primary text-center">
            Cantidades ajustadas para {servings} {servings === 1 ? 'persona' : 'personas'}
          </p>
        </div>
      )}

      <div className="space-y-3">
        {adjustedIngredients?.map((ing, i) => (
          <div 
            key={i} 
            className="flex items-center justify-between py-3 border-b border-stone-50 dark:border-stone-800 last:border-0"
          >
            <span className="text-stone-600 dark:text-stone-300 font-medium">
              {ing.item}
            </span>
            <div className="flex items-center gap-2">
              {servings !== originalServings && (
                <span className="text-[10px] text-stone-400 line-through">
                  {ing.amount} {ing.unit}
                </span>
              )}
              <span className={`font-black px-3 py-1 rounded-lg ${
                servings !== originalServings 
                  ? 'bg-primary/10 text-primary' 
                  : 'bg-stone-100 dark:bg-stone-800 text-stone-700 dark:text-stone-300'
              }`}>
                {ing.adjustedAmount || ing.amount} {ing.unit}
              </span>
            </div>
          </div>
        ))}
      </div>

      {servings !== originalServings && (
        <button
          onClick={() => setServings(originalServings)}
          className="w-full mt-4 py-2 text-[10px] font-black uppercase tracking-widest text-stone-400 hover:text-primary transition-colors"
        >
          Restaurar cantidad original
        </button>
      )}
    </div>
  );
};
